import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Form, Input, Button, Checkbox, Row, Col, Typography, Image, Card, Modal, Carousel, Tooltip } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined, SecurityScanOutlined, ArrowRightOutlined, ControlOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';

import { getPublicTools } from 'api/public';
import { loginUser } from 'api/auth';
import { storeToken } from 'utils/auth';
import { ITool } from 'models/tool';

const { Title, Paragraph } = Typography;

const Login: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const { data: toolsData, run: fetchTools } = useRequest(getPublicTools, { manual: true });
    const navigate = useNavigate();
    const carouselRef = useRef<HTMLDivElement>(null);
    const [showScrollArrow, setShowScrollArrow] = useState(false);
    const [selectedTool, setSelectedTool] = useState<ITool | null>(null);
    const emailInputRef = useRef<any>(null);
    const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

    useEffect(() => {
        fetchTools();
    }, [fetchTools]);

    useEffect(() => {
        const checkScroll = () => {
            if (carouselRef.current) {
                const { scrollWidth, clientWidth } = carouselRef.current;
                setShowScrollArrow(scrollWidth > clientWidth);
            }
        };
        // A small delay to allow the carousel to render fully before checking
        const timer = setTimeout(checkScroll, 100);
        window.addEventListener('resize', checkScroll);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', checkScroll);
        };
    }, [toolsData]);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const { scrollLeft, scrollWidth, clientWidth } = event.currentTarget;
        // Add a small buffer (e.g., 1px) to handle fractional pixel values
        if (scrollLeft + clientWidth >= scrollWidth - 1) {
            setIsScrolledToEnd(true);
        } else {
            setIsScrolledToEnd(false);
        }
    };

    const handleCardClick = (tool: ITool) => {
        setSelectedTool(tool);
    };

    const handleModalClose = () => {
        setSelectedTool(null);
    };

    const handleLoginClick = () => {
        handleModalClose();
        emailInputRef.current?.focus();
    };

    const handleSignUpClick = () => {
        handleModalClose();
        navigate('/auth/register');
    };

    const onFinish = async (values: any) => {
        setLoading(true);
        try {
            const res = await loginUser({ email: values.email, password: values.password });
            storeToken(res.token, values.remember);
            navigate('/tools');
        } catch (error) {
            console.error('Login failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const sortedTools = useMemo(() => {
        if (!toolsData) return [];
        return [...toolsData].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }, [toolsData]);

    return (
        <div className="login-container">
            <div className="login-row">
                <Row>
                    {/* Hero Section */}
                    <Col xs={24} lg={12} className="branding-section">
                        <div className="branding-content">
                            <div className="logo-container">
                                <Image
                                    src={`${process.env.PUBLIC_URL}/logo.png`}
                                    alt="Power Tools Logo"
                                    preview={false}
                                    className="logo-image"
                                />
                                <Title level={2} className="brand-name">Power Tools</Title>
                            </div>
                            <Title level={3} className="brand-tagline">Enhance Your Power Platform Experience</Title>
                            <Paragraph className="brand-description">
                                A suite of tools designed to boost your productivity and streamline your Dynamics 365 / Power Platform workflow.
                            </Paragraph>
                        </div>
                    </Col>
                    <Col xs={24} lg={12} className="form-section" style={{ position: 'relative' }}>
                        <Tooltip title="FAQ">
                            <Link to="/auth/faq" style={{ position: 'absolute', top: '24px', right: '24px', zIndex: 1 }}>
                                <QuestionCircleOutlined style={{ fontSize: '22px', color: '#1890ff' }} />
                            </Link>
                        </Tooltip>
                        <div className="form-container" style={{ padding: '40px', paddingTop: '80px' }}>
                            <Title level={2} className="form-title" style={{ marginTop: 0 }}>Welcome Back</Title>
                            <Form
                                name="login"
                                initialValues={{ remember: true }}
                                onFinish={onFinish}
                                size="large"
                                autoComplete="off"
                            >
                                <Form.Item
                                    name="email"
                                    rules={[{ required: true, message: 'Please input your email!' }]}
                                >
                                    <Input ref={emailInputRef} prefix={<UserOutlined />} placeholder="Email" />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    rules={[{ required: true, message: 'Please input your password!' }]}
                                >
                                    <Input.Password prefix={<LockOutlined />} placeholder="Password" />
                                </Form.Item>

                                <Form.Item>
                                    <div className="form-actions">
                                        <Form.Item name="remember" valuePropName="checked" noStyle>
                                            <Checkbox>Remember me</Checkbox>
                                        </Form.Item>
                                    </div>
                                </Form.Item>

                                <Form.Item>
                                    <Button type="primary" htmlType="submit" className="login-button" loading={loading} block>
                                        Log in
                                    </Button>
                                </Form.Item>
                            </Form>
                            <div className="signup-prompt">
                                <Paragraph>Don't have an account? </Paragraph>
                                <Link to="/auth/register">
                                    <Button type="link" className="signup-link">Sign up</Button>
                                </Link>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Tools Showcase Section */}
                <div className="tools-showcase">
                    <Title level={2} className="tools-title">Powered by the Community</Title>
                    <Paragraph className="tools-description">
                        Explore a growing collection of powerful tools built and shared by developers just like you. From advanced query builders to comprehensive auditing solutions, our community is constantly innovating to enhance the Power Platform experience.
                    </Paragraph>
                    <div style={{ position: 'relative' }}>
                        <div ref={carouselRef} onScroll={handleScroll} className="tools-carousel-container" style={{
                            display: 'flex',
                            overflowX: 'auto',
                            padding: '24px 12px',
                            scrollbarWidth: 'none', /* For Firefox */
                        }}>
                            {sortedTools.map((tool) => (
                                <div key={tool.id} style={{ flex: '0 0 250px', marginRight: '24px' }}>
                                    <Card
                                        hoverable
                                        className="tool-card"
                                        style={{ aspectRatio: '1 / 1', borderRadius: '12px', display: 'flex', flexDirection: 'column' }}
                                        bodyStyle={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
                                        onClick={() => handleCardClick(tool)}
                                    >
                                        <Title level={4} className="tool-title">{tool.name}</Title>
                                        <Paragraph className="tool-description" ellipsis={{ rows: 4 }}>{tool.description}</Paragraph>
                                    </Card>
                                </div>
                            ))}
                        </div>
                        {showScrollArrow && !isScrolledToEnd && (
                            <div style={{
                                position: 'absolute',
                                right: '20px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '24px',
                                color: 'rgba(0, 0, 0, 0.45)',
                                pointerEvents: 'none',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)',
                                padding: '20px',
                                borderRadius: '50%'
                            }}>
                                <ArrowRightOutlined />
                            </div>
                        )}
                    </div>
                </div>

                {/* Security Sections */}
                <div className="security-info-sections">
                    {/* Microsoft Verified Section */}
                    <div style={{ padding: '64px 24px', background: '#e6f7ff' }}>
                        <Row justify="center" align="middle" gutter={[32, 32]}>
                            <Col xs={24} md={4} style={{ textAlign: 'center' }}>
                                <SafetyCertificateOutlined style={{ fontSize: '80px', color: '#52c41a' }} />
                            </Col>
                            <Col xs={24} md={12}>
                                <Title level={3}>Verified by Microsoft</Title>
                                <Paragraph style={{ fontSize: '16px' }}>
                                    Power Tools is a verified publisher in the Microsoft Cloud Partner Program. This verification process provides a higher level of trust and assurance that the application comes from a legitimate source.
                                </Paragraph>
                            </Col>
                        </Row>
                    </div>

                    {/* Control & Transparency Section */}
                    <div style={{ padding: '48px 24px', background: '#003a8c', color: 'white' }}>
                        <Row justify="center" gutter={[48, 48]} align="top">
                            <Col xs={24} md={11}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '24px', height: '100%' }}>
                                    <ControlOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                                    <div>
                                        <Title level={3} style={{ color: 'white', marginTop: 0 }}>Full Administrative Control</Title>
                                        <Paragraph style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                                            In many organizations, administrator consent is required before an application can be granted access, giving your IT department full oversight. As a registered Azure application, Power Tools allows administrators to revoke access at any time from the Azure portal.
                                        </Paragraph>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} md={11}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '24px', height: '100%' }}>
                                    <SecurityScanOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                                    <div>
                                        <Title level={3} style={{ color: 'white', marginTop: 0 }}>Transparency</Title>
                                        <Paragraph style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                                            When you connect to an environment, the specific permissions required are clearly displayed on the Microsoft consent screen for your review. The application operates on the principle of least privilege and can never perform actions your user account is not permitted to do.
                                        </Paragraph>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>
            <Modal
                title={selectedTool?.name}
                visible={!!selectedTool}
                onCancel={handleModalClose}
                footer={[
                    <Button key="signup" type="ghost" onClick={handleSignUpClick}>
                        Sign Up
                    </Button>,
                    <Button key="login" type="primary" onClick={handleLoginClick}>
                        Login to Use
                    </Button>,
                ]}
                width={800}
                centered
                wrapClassName="tool-details-modal"
            >
                {selectedTool?.media && selectedTool.media.length > 0 && (
                    <Carousel autoplay>
                        {selectedTool.media.map(item => (
                            <div key={item.id}>
                                <Image
                                    width="100%"
                                    src={item.url}
                                    alt={`${selectedTool.name} media`}
                                    preview={false}
                                />
                            </div>
                        ))}
                    </Carousel>
                )}
                <Paragraph style={{ marginTop: '24px' }}>
                    {selectedTool?.longDescription || selectedTool?.description}
                </Paragraph>
            </Modal>
        </div>
    );
};

export default Login;
